import React, { useCallback, useEffect, useState } from "react"

import { Loader2, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

type Row = Record<string, unknown>

const Dsp: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([])
  const [error, setError] = useState<string | null>(null)
  const [syncRunning, setSyncRunning] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  const [search, setSearch] = useState("")
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all")

  const fetchData = useCallback(async () => {
    setError(null)

    try {
      let query = supabase
        .from("dsps")
        .select("id, dsp, legal_name, notes, airtable_record_id, active")

      if (search.trim()) {
        const like = `%${search}%`
        query = query.or(`dsp.ilike.${like},legal_name.ilike.${like},notes.ilike.${like}`)
      }

      // Apply active filter
      if (activeFilter === "active") {
        query = query.eq("active", true)
      } else if (activeFilter === "inactive") {
        query = query.eq("active", false)
      }
      // "all" means no filter

      const { data, error } = await query.order("active", { ascending: false }).order("dsp")
      if (error) throw error

      setRows(data || [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.message)
    }
  }, [search, activeFilter])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const runSync = async () => {
    if (syncRunning) return
    setSyncRunning(true)
    setSyncMessage(null)

    try {
      await supabase.functions.invoke("airtable-sync-dsp")
      setSyncMessage("Sync completed")
      await fetchData()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setSyncMessage(`Sync failed: ${e.message}`)
    } finally {
      setSyncRunning(false)
    }
  }

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      // Optimistic update
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === id ? { ...row, active: !currentActive } : row)),
      )
      const { error } = await supabase
        .from("dsps")
        .update({ active: !currentActive })
        .eq("id", id)
        .select("*")
        .maybeSingle()
      if (error) {
        // Revert on error
        setRows((prevRows) =>
          prevRows.map((row) => (row.id === id ? { ...row, active: currentActive } : row)),
        )
        throw error
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(`Failed to toggle active: ${e.message}`)
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-[95vw] mx-auto p-4 md:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">DSP</h1>
          <p className="text-muted-foreground">
            Browse DSP records. Sync to fetch latest from Airtable.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardDescription>Showing: {rows.length}</CardDescription>

            <div className="mt-3 flex gap-3 items-center flex-wrap">
              <div className="relative w-64">
                <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-md border pl-8 pr-2 py-1 bg-background"
                />
              </div>

              <Select
                value={activeFilter}
                onValueChange={(value: "all" | "active" | "inactive") => setActiveFilter(value)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>

              <Button onClick={runSync} disabled={syncRunning}>
                {syncRunning ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Syncing…
                  </span>
                ) : (
                  "Sync from Airtable"
                )}
              </Button>

              {syncMessage && <div className="text-sm">{syncMessage}</div>}
            </div>
          </CardHeader>

          <CardContent>
            {error ? (
              <div className="text-red-600">{error}</div>
            ) : (
              <div className="w-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>DSP</TableHead>
                      <TableHead>Legal Name</TableHead>
                      <TableHead>Airtable Record Id</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Active</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i} className={row.active ? "" : "opacity-50"}>
                        <TableCell>{String(row.dsp ?? "")}</TableCell>
                        <TableCell>{String(row.legal_name ?? "")}</TableCell>
                        <TableCell>{String(row.airtable_record_id ?? "")}</TableCell>
                        <TableCell>{String(row.notes ?? "")}</TableCell>
                        <TableCell>
                          <Switch
                            checked={Boolean(row.active)}
                            onCheckedChange={() =>
                              toggleActive(String(row.id), Boolean(row.active))
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default Dsp
